import type { ReactNode } from 'react';
import { createContext, useContext, useMemo } from 'react';
import { useDataFetcher } from '@/shared/hooks';
import type { Highlight } from '../../model/types';
import { ValueType } from '../../model/types';
import { useDataFetchersContext } from '../DataFetchersContext/DataFetchersContext';

interface ContextState {
  min?: number;
  max?: number;
}

const Context = createContext<ContextState | null>(null);

interface Props {
  highlight: Highlight;
  children: ReactNode;
}

export function HighlightMinMaxProvider({ children, highlight }: Props) {
  const { onGetMinMax } = useDataFetchersContext();
  const { data: minMax } = useDataFetcher(onGetMinMax);
  const { min, max } = useMemo<{ min?: number; max?: number }>(() => {
    if (highlight.valueType === ValueType.percent) {
      return {
        min: 0,
        max: 1,
      };
    }

    return minMax ?? {};
  }, [highlight, minMax]);

  return <Context.Provider value={{ min, max }}>{children}</Context.Provider>;
}

export const useHighlightMinMaxContext = () => {
  const context = useContext(Context);

  if (!context) {
    throw new Error(
      'useHighlightMinMaxContext must be used within a HighlightMinMaxProvider',
    );
  }

  return context;
};
